/**
 * The post-answer view.
 *
 * Shows the correct answer AND, when the answer was only accepted because
 * accents were folded, which accented form it was. That second line is the
 * whole reason this is not the shared grammar back: the comparison forgives a
 * missing `ã` because her keyboard has none, and forgiving it silently would
 * mean she never learns where it goes.
 */
import { CheckCircle2, XCircle } from 'lucide-react';

import { cn } from '@/lib/utils';

import { correctAnswerText } from '../shared/grammar/types';
import type { CardBackProps } from '../types';

import { accentNear } from './answer';
import { toExercise } from './course';
import type { GramaticaPayload } from './types';

export function CardBack({
  card,
  correct,
  user_answer,
  isLastCard,
  onNext,
}: CardBackProps<GramaticaPayload>) {
  const exercise = toExercise(card.payload);
  // When several orders are correct, show the one the user actually built.
  // Printing the canonical answer over a different-but-accepted sentence reads
  // as "right, but not that" — the opposite of what `also_accept` is for.
  const answer = correct && user_answer ? user_answer : correctAnswerText(exercise);
  const accented =
    correct && user_answer && exercise.kind !== 'order'
      ? accentNear(user_answer, [exercise.answer, ...('accept' in exercise ? exercise.accept : [])])
      : null;

  return (
    <div className="border-border bg-card flex flex-col gap-6 rounded-2xl border p-6 shadow-sm sm:p-8">
      <div className="flex items-center gap-3">
        {correct ? (
          <CheckCircle2 className="text-success size-6" aria-hidden />
        ) : (
          <XCircle className="text-destructive size-6" aria-hidden />
        )}
        <span className={cn('font-medium', correct ? 'text-success' : 'text-destructive')}>
          {correct ? 'Certo' : user_answer === null ? 'Pulou' : 'Quase'}
        </span>
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
          Resposta
        </span>
        <p className="font-display text-2xl">{accented ?? answer}</p>
        {accented && (
          <p className="text-muted-foreground text-sm">
            Accepted without the accent — it is written <strong>{accented}</strong>.
          </p>
        )}
        {!correct && user_answer !== null && (
          <p className="text-muted-foreground text-sm">
            You wrote <span className="line-through">{user_answer}</span>
          </p>
        )}
      </div>

      <button
        type="button"
        onClick={onNext}
        className="bg-primary text-primary-foreground focus-visible:ring-ring min-h-12 w-full rounded-lg px-4 font-medium focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
      >
        {isLastCard ? 'Terminar' : 'Próximo'}
      </button>
    </div>
  );
}
